import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';

import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import { styled } from '@mui/material/styles';
import Alert from '@mui/material/Alert';
import get from 'lodash/get';

import { uploadImage } from '@src/services/api/images';
import { formatBytes } from '@src/utils/number-helpers';

import Dropzone from './image-dropzone';

const StyledBackdrop = styled(Backdrop)(({ theme }) => ({
    color: '#fff',
    zIndex: theme?.zIndex?.drawer ? theme.zIndex.drawer + 1 : 1301,
    position: 'absolute',
}));

const ImageUpload = (props) => {
    const { children, folder, onImageUploaded } = props;

    const [error, setError] = useState(null);
    const [isUploading, setIsUploading] = useState(false);

    const handleImageUploadError = useCallback((error) => {
        let errorMessage = error.messsage || 'Unknown error';

        const responseMessage = get(error, ['response', 'data', 'error', 'message']);
        if (responseMessage) {
            errorMessage = responseMessage.replace(
                /(\d+(?:((?=\.))\d*))/gm,
                (match) => formatBytes(match),
            );
        }

        setIsUploading(false);
        setError(errorMessage);
    }, [setIsUploading, setError]);

    const handleImageUploadSuccess = useCallback((imageData) => {
        setIsUploading(false);
        setError(null);

        onImageUploaded(imageData);
    }, [setIsUploading, setError, onImageUploaded]);

    const handleFileSelection = useCallback((imageFile) => {
        setIsUploading(true);
        uploadImage(imageFile, folder)
            .then((imageData) => {
                handleImageUploadSuccess(imageData);
            }).catch((error) => {
                handleImageUploadError(error);
            });
    }, [folder, setIsUploading, handleImageUploadSuccess, handleImageUploadError]);

    const handleAlertClose = useCallback(() => {
        setError(null);
    }, [setError]);

    if (isUploading) {
        return (
            <>
                <StyledBackdrop open>
                    <CircularProgress color="inherit" />
                </StyledBackdrop>
                { children }
            </>
        );
    }

    return (
        <>
            <Dropzone onFileSelected={handleFileSelection}>
                { children }
            </Dropzone>
            <Snackbar open={Boolean(error)} autoHideDuration={6000} onClose={handleAlertClose}>
                <Alert severity="error" onClose={handleAlertClose} >
                    { error }
                </Alert>
            </Snackbar>
        </>
    );
};

ImageUpload.propTypes = {
    children: PropTypes.node,
    folder: PropTypes.string.isRequired,
    onImageUploaded: PropTypes.func.isRequired,
};

export default ImageUpload;
